/**
 * useImageGeneration hook - Manages multi-variant image generation with rounds.
 *
 * Provides:
 * - Round-based generation (1-5 rounds: 1 initial + 4 refinements)
 * - 4 image variations per round
 * - Selection tracking for refinement chain
 * - Grounding image-to-image support
 * - Progress tracking
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  getApiClient,
  type GenerateImagesRequest,
  type GenerateImagesResponse,
  type ImageVariation,
} from '@/services/api'
import type { GenerationStatus } from '@/components/image/GenerationProgress'

/**
 * Maximum number of rounds (1 initial + 4 refinements)
 */
const MAX_ROUNDS = 5

/**
 * State for a single generation round
 */
export interface RoundState {
  roundNumber: number
  variations: ImageVariation[]
  selectedVariantIndex: number | null
  selectedImageId: string | null
  promptUsed: string
  generationDurationMs: number
  groundingApplied?: boolean | null
  generationMode?: string | null
}

/**
 * Image generation state
 */
export interface ImageGenerationState {
  status: GenerationStatus
  currentRound: number
  maxRounds: number
  rounds: RoundState[]
  currentVariations: ImageVariation[]
  selectedVariation: ImageVariation | null
  canRefine: boolean
  isLoading: boolean
  error: string | null
  elapsedMs: number
}

/**
 * Image generation actions
 */
export interface ImageGenerationActions {
  generateInitial: (feedback?: string, promptOverride?: string, groundingImageId?: string) => Promise<boolean>
  refine: (feedback: string) => Promise<boolean>
  selectVariation: (variantIndex: number) => void
  accept: () => ImageVariation | null
  reset: () => void
  cancel: () => void
  goBack: () => void
  navigateToRound: (roundIndex: number) => void
}

/**
 * Additional navigation state
 */
export interface ImageGenerationNavigation {
  canGoBack: boolean
}

/**
 * useImageGeneration hook return type
 */
export interface UseImageGenerationReturn extends ImageGenerationState, ImageGenerationActions, ImageGenerationNavigation {}

/**
 * Hook for managing multi-variant image generation
 */
export function useImageGeneration(sessionId: string | null): UseImageGenerationReturn {
  const [status, setStatus] = useState<GenerationStatus>('queued')
  const [rounds, setRounds] = useState<RoundState[]>([])
  const [currentRound, setCurrentRound] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  const timerRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)
  const cancelledRef = useRef<boolean>(false)
  // Incremented on every generateInitial/refine call — stale async closures check this
  // to avoid writing error/status state from a superseded request.
  const generationIdRef = useRef<number>(0)

  const apiClient = getApiClient()

  // Derived values — memoised to give stable references for React.memo children
  const currentVariations = useMemo(() => {
    const latestRound = rounds[rounds.length - 1]
    return latestRound?.variations ?? []
  }, [rounds])

  const selectedVariation = useMemo(() => {
    const latestRound = rounds[rounds.length - 1]
    if (!latestRound || latestRound.selectedVariantIndex === null) return null
    const idx = latestRound.selectedVariantIndex
    return (idx >= 0 && idx < currentVariations.length)
      ? currentVariations[idx] ?? null
      : null
  }, [rounds, currentVariations])

  const canRefine = currentRound < MAX_ROUNDS && selectedVariation !== null && !isLoading

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        cancelAnimationFrame(timerRef.current)
      }
    }
  }, [])

  /**
   * Start the elapsed time tracker using requestAnimationFrame.
   * Synchronises updates with browser paint cycle for smoother visuals.
   * Browser automatically pauses updates when tab is inactive.
   */
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now()

    const updateElapsed = () => {
      if (timerRef.current !== null) {
        setElapsedMs(Date.now() - startTimeRef.current)
        timerRef.current = requestAnimationFrame(updateElapsed)
      }
    }

    timerRef.current = requestAnimationFrame(updateElapsed)
  }, [])

  /**
   * Stop the elapsed time tracker
   */
  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current)
      timerRef.current = null
    }
  }, [])

  /**
   * Process generation response into round state
   */
  const processResponse = useCallback((response: GenerateImagesResponse): RoundState => {
    return {
      roundNumber: response.round_number,
      variations: response.image_options,
      selectedVariantIndex: null,
      selectedImageId: null,
      promptUsed: response.prompt_used,
      generationDurationMs: response.generation_duration_ms,
      groundingApplied: response.grounding_applied,
      generationMode: response.generation_mode,
    }
  }, [])

  /**
   * Generate initial images (round 1).
   * @param feedback - User prompt text; defaults to a generic fallback if omitted.
   * @param promptOverride - Facilitator-edited vision description; bypasses AI theme extraction.
   * @param groundingImageId - Grounding image ID; triggers image-to-image mode from location photos.
   */
  const generateInitial = useCallback(
    async (feedback?: string, promptOverride?: string, groundingImageId?: string): Promise<boolean> => {
      if (!sessionId) {
        setError('No active session')
        return false
      }

      // Stamp this generation so stale timeout catches don't overwrite newer state
      const myGenId = ++generationIdRef.current

      // Reset state for new generation
      cancelledRef.current = false
      setIsLoading(true)
      setError(null)
      setStatus('queued')
      setRounds([])
      setCurrentRound(0)
      setElapsedMs(0)
      startTimer()

      try {
        setStatus('generating')

        const request: GenerateImagesRequest = {
          feedback: feedback || 'Generate an image based on our conversation',
        }

        // Add facilitator-edited vision description if provided
        if (promptOverride) {
          request.prompt_override = promptOverride
        }

        // Add grounding image ID for image-to-image from location photos
        if (groundingImageId) {
          request.grounding_image_id = groundingImageId
        }

        const response = await apiClient.generateImages(sessionId, request)

        // Check if cancelled during request
        if (cancelledRef.current) {
          stopTimer()
          return false
        }

        setStatus('validating')

        // Short delay to show validating status
        await new Promise((resolve) => setTimeout(resolve, 300))

        if (cancelledRef.current || generationIdRef.current !== myGenId) {
          stopTimer()
          return false
        }

        // Success - store round state
        const roundState = processResponse(response)
        setRounds([roundState])
        setCurrentRound(response.round_number)
        setStatus('complete')
        setIsLoading(false)
        stopTimer()

        return true
      } catch (err) {
        // Ignore errors from superseded requests — a newer generateInitial call
        // already owns the state, so writing here would corrupt it.
        if (generationIdRef.current !== myGenId) {
          stopTimer()
          return false
        }
        const errorMessage = err instanceof Error ? err.message : 'Image generation failed'
        setError(errorMessage)
        setStatus('failed')
        setIsLoading(false)
        stopTimer()
        return false
      }
    },
    [sessionId, apiClient, startTimer, stopTimer, processResponse]
  )

  /**
   * Refine with feedback using selected image as anchor
   */
  const refine = useCallback(
    async (feedback: string): Promise<boolean> => {
      if (!sessionId) {
        setError('No active session')
        return false
      }

      if (!selectedVariation) {
        setError('Please select an image to refine')
        return false
      }

      if (currentRound >= MAX_ROUNDS) {
        setError('Maximum refinement rounds reached')
        return false
      }

      if (!feedback.trim()) {
        setError('Please provide feedback for refinement')
        return false
      }

      // Stamp this generation so stale timeout catches don't overwrite newer state
      const myGenId = ++generationIdRef.current

      // Reset generation state
      cancelledRef.current = false
      setIsLoading(true)
      setError(null)
      setStatus('queued')
      setElapsedMs(0)
      startTimer()

      try {
        setStatus('generating')

        const request: GenerateImagesRequest = {
          feedback: feedback.trim(),
          selected_image_id: selectedVariation.image_id,
        }

        const response = await apiClient.generateImages(sessionId, request)

        // Check if cancelled during request
        if (cancelledRef.current) {
          stopTimer()
          return false
        }

        setStatus('validating')

        // Short delay to show validating status
        await new Promise((resolve) => setTimeout(resolve, 300))

        if (cancelledRef.current || generationIdRef.current !== myGenId) {
          stopTimer()
          return false
        }

        // Success - add new round state
        const roundState = processResponse(response)
        setRounds((prev) => [...prev, roundState])
        setCurrentRound(response.round_number)
        setStatus('complete')
        setIsLoading(false)
        stopTimer()

        return true
      } catch (err) {
        if (generationIdRef.current !== myGenId) {
          stopTimer()
          return false
        }
        const errorMessage = err instanceof Error ? err.message : 'Image refinement failed'
        setError(errorMessage)
        setStatus('failed')
        setIsLoading(false)
        stopTimer()
        return false
      }
    },
    [sessionId, apiClient, selectedVariation, currentRound, startTimer, stopTimer, processResponse]
  )

  /**
   * Select a variation from the current round
   */
  const selectVariation = useCallback(
    (variantIndex: number) => {
      if (variantIndex < 0 || variantIndex >= 4) {
        return
      }

      setRounds((prev) => {
        if (prev.length === 0) return prev

        const updated = [...prev]
        const lastRoundOriginal = updated[updated.length - 1]
        if (!lastRoundOriginal) return prev

        const selectedVar = lastRoundOriginal.variations[variantIndex]

        const lastRound: RoundState = {
          ...lastRoundOriginal,
          selectedVariantIndex: variantIndex,
          selectedImageId: selectedVar?.image_id ?? null,
        }
        updated[updated.length - 1] = lastRound

        return updated
      })
    },
    []
  )

  /**
   * Accept the currently selected variation
   * Returns the selected variation or null if none selected
   */
  const accept = useCallback((): ImageVariation | null => {
    if (!selectedVariation) {
      setError('Please select an image to accept')
      return null
    }

    // Clear any errors
    setError(null)

    return selectedVariation
  }, [selectedVariation])

  /**
   * Reset generation state
   */
  const reset = useCallback(() => {
    stopTimer()
    cancelledRef.current = false
    setStatus('queued')
    setRounds([])
    setCurrentRound(0)
    setIsLoading(false)
    setError(null)
    setElapsedMs(0)
  }, [stopTimer])

  /**
   * Cancel ongoing generation
   */
  const cancel = useCallback(() => {
    cancelledRef.current = true
    setIsLoading(false)
    setStatus('queued')
    stopTimer()
  }, [stopTimer])

  /**
   * Go back to previous round, discarding current round.
   * Allows user to re-select a different image from the previous round.
   *
   * Clears the selection on the restored round, forcing the user to
   * make a new selection. Silently no-ops if at round 1 or if loading.
   */
  const goBack = useCallback(() => {
    if (rounds.length <= 1 || isLoading) {
      return
    }

    // Single atomic update: remove last round AND clear selection on new last round
    // This avoids race conditions from calling setRounds() twice
    setRounds((prev) => {
      if (prev.length <= 1) return prev

      // Remove the last round
      const updated = prev.slice(0, -1)

      // Clear selection on the now-last round to force re-selection
      const lastRound = updated[updated.length - 1]
      if (lastRound) {
        updated[updated.length - 1] = {
          ...lastRound,
          selectedVariantIndex: null,
          selectedImageId: null,
        }
      }

      return updated
    })

    // Derive new current round from the reduced rounds array
    setCurrentRound((prev) => Math.max(1, prev - 1))

    // Clear any errors
    setError(null)
    setStatus('complete')
  }, [rounds.length, isLoading])

  /**
   * Navigate to a specific round, truncating all rounds after it.
   * Clears the selection on the target round to force re-selection.
   * Allows branching from any previous round in the timeline.
   *
   * No-ops if loading, if roundIndex is out of bounds, or if
   * roundIndex points to the current last round (nothing to truncate).
   */
  const navigateToRound = useCallback((roundIndex: number) => {
    if (isLoading) {
      console.warn('[navigateToRound] Rejected: generation in progress')
      return
    }
    if (roundIndex < 0 || roundIndex >= rounds.length) {
      console.warn(`[navigateToRound] Rejected: roundIndex ${roundIndex} out of bounds (0..${rounds.length - 1})`)
      return
    }
    // No-op if already at this round (it's the last one)
    if (roundIndex === rounds.length - 1) {
      console.warn(`[navigateToRound] Rejected: already at round index ${roundIndex}`)
      return
    }

    setRounds((prev) => {
      // Truncate to include rounds up to and including roundIndex
      const truncated = prev.slice(0, roundIndex + 1)

      // Clear selection on the target round to force re-selection
      const targetRound = truncated[truncated.length - 1]
      if (targetRound) {
        truncated[truncated.length - 1] = {
          ...targetRound,
          selectedVariantIndex: null,
          selectedImageId: null,
        }
      }

      return truncated
    })

    // Update current round to match the target round's number
    const targetRound = rounds[roundIndex]
    if (targetRound) {
      setCurrentRound(targetRound.roundNumber)
    }

    setError(null)
    setStatus('complete')
  }, [rounds, isLoading])

  /**
   * Whether user can navigate back to a previous round
   */
  const canGoBack = rounds.length > 1 && !isLoading

  return {
    // State
    status,
    currentRound,
    maxRounds: MAX_ROUNDS,
    rounds,
    currentVariations,
    selectedVariation,
    canRefine,
    isLoading,
    error,
    elapsedMs,
    // Navigation
    canGoBack,
    // Actions
    generateInitial,
    refine,
    selectVariation,
    accept,
    reset,
    cancel,
    goBack,
    navigateToRound,
  }
}

export default useImageGeneration
